/* ============================================================
   Privacy Notice, Terms of Use, FAQ.

   Structure and legal framing benchmarked against the sister JK &
   Associates site, which aligns its privacy notice to the Data
   Protection Act 2019 (Kenya) and names the ODPC.

   Kanda's position is materially stronger than a typical site's and the
   notice says so precisely rather than vaguely: there is no server, so
   there is no collection to disclose, no processor to name and no
   transfer to justify. The honest consequence — that we therefore
   cannot recover anything for you either — is stated in the same
   breath, because a privacy claim that only lists benefits is
   marketing.

   There is no company registration number yet. Rather than invent one
   or leave a gap, these pages state the position: incorporation in
   progress, number to follow.

   That sentence was for a while wrapped in a `.todo` highlight, left
   over from an earlier draft where it genuinely was a blank. It is not
   a blank, and dressing a finished statement as an unfilled field told
   readers the notice had been abandoned half-written — in the section
   the Data Protection Act requires to identify the controller, which is
   the worst place on this site to look unserious. Plain text now.
   ============================================================ */

import { SITE } from '../data/site.js';
import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { router } from '../shared/router.js';

function page(title, subtitle, body) {
  const node = document.createElement('div');
  node.className = 'container-narrow page animate-fade-in';
  node.innerHTML = html`
    <header class="page-head">
      <h1>${title}</h1>
      <p>${subtitle}</p>
    </header>
    <article class="prose">${raw(body)}</article>
  `;
  return node;
}

function toc(items) {
  return html`
    <nav class="toc" aria-label="Contents">
      <ol>
        <!-- A real fragment, not a self-link. This read
             location.hash under the old hash router; since the move to
             path routing location.hash is always '' , so every one of
             these shipped as href="" — inert without JavaScript, and a
             page of empty hrefs in the HTML a crawler is served. The
             click handler still preventDefaults for the smooth scroll.

             The numbering here is positional and the headings below are
             hand-numbered, so inserting a section is two edits that must
             agree. A smoke test asserts they do; adding one and
             forgetting the other produced a duplicate #s7 once already. -->
        ${items.map((t, i) => html`<li><a href="#s${i + 1}" data-jump="s${i + 1}">${t}</a></li>`)}
      </ol>
    </nav>
  `;
}

/* ============================================================
   Privacy
   ============================================================ */

export function PrivacyPage() {
  const sections = [
    'Who we are',
    'How the tools handle your data',
    'What we actually receive',
    'Storage on your device',
    'Links you choose to share',
    'Processors and third parties',
    'International transfers',
    'Retention and deletion',
    'Your rights',
    'Complaints',
    'Children',
    'Security',
    'Changes',
    'Contact'
  ];

  const node = page(
    'Privacy Notice',
    `Last updated ${SITE.lastUpdated} · Aligned with the ${SITE.dataProtectionAct}`,
    html`
      <div class="notice notice-info" style="margin-bottom: var(--ca-space-6);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('lock', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">The short version.</strong>
          Every tool on this site runs inside your web browser. Anything you type — scorecard
          answers, cost figures, checklist ticks, barrier reports — is stored only on your own
          device and is never transmitted to us or to anyone else. We operate no server that
          could receive it, so we cannot see it, cannot recover it, and cannot hand it to a
          third party. The only personal data we ever hold is what you choose to put in an
          email to us. One thing can leave your device, and only if you make it: a link to a
          corridor run, which carries the figures you typed. Section 5 says exactly what is in
          one.
        </div>
      </div>

      ${raw(toc(sections))}

      <h2 id="s1">1. Who we are</h2>
      <p>
        This site is operated by ${SITE.publishedName} (“Kanda”, “we”, “us”), a logistics advisory
        practice based in ${SITE.location}. For the purposes of the
        ${SITE.dataProtectionAct} we are the data controller for the limited personal data
        described below.
      </p>
      <ul>
        <li>Registered entity: ${SITE.registeredEntity}</li>
        <li>Registered address: ${SITE.registeredAddress}</li>
        <li>Email: <a href="mailto:${SITE.email}">${SITE.email}</a></li>
      </ul>
      <p>
        We have not appointed a Data Protection Officer. Given that the tools process no
        personal data on our infrastructure, we do not currently meet the threshold at which
        one is required, and data-protection queries are handled directly at the address above.
        We keep that position under review as the practice grows.
      </p>

      <h2 id="s2">2. How the tools handle your data</h2>
      <p>
        Kanda is a static application. Once the page has loaded, it makes no network requests
        at all. That is not a policy we ask you to trust — it is enforced by a Content Security
        Policy served with the site, which blocks requests to every origin other than our own.
        You can verify it yourself: open your browser's network inspector, complete a
        diagnostic, and observe that nothing is sent.
      </p>
      <p>
        The practical consequence runs both ways, and it is only fair to state the second half.
        Because we hold nothing, we can offer no account recovery, no sync between your phone
        and your laptop, and no restoration if you clear your browser data or lose the device.
        Export anything you would be sorry to lose.
      </p>

      <h2 id="s3">3. What we actually receive</h2>
      <p>
        Only what you send us. If you email us, we receive your address, your name if you give
        it, and whatever you write. We use it to answer you and for no other purpose. We do not
        operate a mailing list, we run no analytics, we set no cookies, and we embed no
        third-party scripts, pixels or fonts.
      </p>

      <h2 id="s4">4. Storage on your device</h2>
      <p>
        The application uses two browser storage mechanisms, both local to your device:
      </p>
      <ul>
        <li>
          <strong style="font-weight: var(--ca-weight-medium);">Local storage</strong> — small
          preferences such as your selected brand and your progress through a checklist.
        </li>
        <li>
          <strong style="font-weight: var(--ca-weight-medium);">IndexedDB</strong> — completed
          scorecards, calculations and barrier reports, so you can return to them.
        </li>
      </ul>
      <p>
        Both are visible to you and erasable by you at any time under
        <button class="link-inline" data-nav="/saved" type="button">Saved work</button>, or by
        clearing site data in your browser settings. Neither is readable by us.
      </p>

      <h2 id="s5">5. Links you choose to share</h2>
      <p>
        The Corridor Run can produce a link to a run. Everything above stays true of it — the
        link is assembled in your browser, nothing is sent to us to create it, and we never see
        that a link exists. But the link is the one thing on this site that can carry your
        figures off your device, and it does so entirely by your own act, so it is worth being
        precise about what it contains.
      </p>
      <ul>
        <li>
          It carries <strong style="font-weight: var(--ca-weight-medium);">the figures you
          typed</strong> — including what the run costs you and what you charge for it. Anyone
          holding the link can read your margin on that lane.
        </li>
        <li>
          It carries <strong style="font-weight: var(--ca-weight-medium);">no results</strong>.
          The receiving device recomputes from those figures, so a link cannot show numbers this
          tool would no longer produce.
        </li>
        <li>
          It carries nothing else — no identifier, no saved history, no record of any other run.
        </li>
      </ul>
      <p>
        A link pasted into a group chat or an email thread is in the hands of everyone on it,
        and may be retained by whichever service carries it. That is outside our control and
        outside this notice. The button that creates a link says so at the point you press it.
      </p>

      <h2 id="s6">6. Processors and third parties</h2>
      <p>
        We engage no data processors, because there is no data to process. The site is served
        as static files by a hosting provider, which will see standard web-server request logs
        — your IP address, the time of the request and the file requested — in the same way any
        website's host does. Those logs are the host's, are not linked to anything you type,
        and are not accessible to us in a form that identifies you.
      </p>

      <h2 id="s7">7. International transfers</h2>
      <p>
        No personal data you enter leaves your device, so no transfer occurs. Static files may
        be served from a content delivery network with points of presence outside Kenya; that
        affects only the delivery of the application itself, never your inputs.
      </p>

      <h2 id="s8">8. Retention and deletion</h2>
      <p>
        Data on your device is retained until you delete it — we set no expiry, because we
        cannot reach it. Email correspondence is retained for as long as needed to deal with
        the matter and for a reasonable period afterwards for our own records.
      </p>

      <h2 id="s9">9. Your rights</h2>
      <p>
        Under the ${SITE.dataProtectionAct} you have rights of access, correction, erasure,
        objection, restriction and portability over personal data a controller holds about you.
      </p>
      <p>
        For anything you have entered into the tools, those rights are already in your hands
        and require no request to us: view it, export it as JSON, or erase it, all from
        <button class="link-inline" data-nav="/saved" type="button">Saved work</button>. For
        email correspondence, write to <a href="mailto:${SITE.email}">${SITE.email}</a> and we
        will respond within the statutory period.
      </p>

      <h2 id="s10">10. Complaints</h2>
      <p>
        If you are not satisfied with how we have handled a data-protection matter, you may
        complain to the ${SITE.regulator}, the supervisory authority for data protection in
        ${SITE.jurisdiction}. We would rather hear from you first, but that route is yours
        regardless.
      </p>

      <h2 id="s11">11. Children</h2>
      <p>
        These are professional tools for commercial operators. They are not directed at
        children and we do not knowingly process children's data.
      </p>

      <h2 id="s12">12. Security</h2>
      <p>
        The site is served over HTTPS with a restrictive Content Security Policy, strict
        referrer policy and content-type protections. The strongest security control here,
        though, is architectural rather than configurational: a system that never receives
        your data cannot leak it. What remains is your device — if it is shared, treat
        anything saved here as visible to whoever else uses it.
      </p>

      <h2 id="s13">13. Changes</h2>
      <p>
        We will update this notice when the product changes. If we ever introduce a feature
        that transmits data — the opt-in benchmark contribution described in our roadmap is the
        only one contemplated — it will be explicitly opt-in, described here before it ships,
        and it will never transmit anything that identifies you or your customers.
      </p>

      <h2 id="s14">14. Contact</h2>
      <p>
        <a href="mailto:${SITE.email}">${SITE.email}</a> · ${SITE.location}
      </p>
    `
  );

  wireLinks(node);
  return node;
}

/* ============================================================
   Terms
   ============================================================ */

export function TermsPage() {
  const node = page(
    'Terms of Use',
    `Last updated ${SITE.lastUpdated} · Governed by the laws of ${SITE.jurisdiction}`,
    html`
      <div class="notice notice-info" style="margin-bottom: var(--ca-space-6);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">In short.</strong>
          These tools are free and provided for general orientation. Their outputs — scores,
          landed costs, benchmarks, emissions figures — are indicative, derived from the numbers
          you enter and from published references. They are not professional advice and you
          should not act on them alone.
        </div>
      </div>

      <h2 id="t1">1. Acceptance</h2>
      <p>
        By using this site you agree to these terms. If you use it on behalf of an
        organisation, you confirm you are authorised to accept them for that organisation. If
        you do not agree, please do not use the site.
      </p>

      <h2 id="t2">2. Who we are</h2>
      <p>
        ${SITE.publishedName}, a logistics advisory practice based in ${SITE.location}. Registered
        entity: ${SITE.registeredEntity}. Contact:
        <a href="mailto:${SITE.email}">${SITE.email}</a>.
      </p>

      <h2 id="t3">3. Purpose — and no professional advice</h2>
      <p>
        Every tool here — the Corridor Health Scorecard, Cost-per-Ton-Km Calculator, Landed
        Cost &amp; Demurrage Guard, Route Profitability Estimator, Financeability Pack,
        Corridor Emissions, NTB Reporter, Compliance Calendar, Data Readiness checklist,
        Licensing Navigator, Postholder Planner and SEZ/EPZ Guide — is a free educational
        orientation tool.
      </p>
      <p>
        None of it is professional, financial, legal, accounting, tax, customs, safety or
        regulatory advice, and using it creates no adviser–client relationship. In particular:
      </p>
      <ul>
        <li>
          Duty and levy calculations are a model of the published order of assessment. They are
          not a customs entry and will not match one in every case. Reconcile against your
          actual entry and your licensed clearing agent.
        </li>
        <li>
          Regulatory status is computed from effective dates we have recorded. Verify anything
          consequential against the gazette notice itself.
        </li>
        <li>
          Benchmarks are derived from published guidance, not observed transactions, and are
          labelled as such where they appear.
        </li>
        <li>
          The financeability score is not a credit decision and no lender is bound by it.
        </li>
        <li>
          Emissions figures follow the GLEC Framework and ISO 14083 method but are not a
          verified or assured statement unless independently audited.
        </li>
      </ul>

      <h2 id="t4">4. No warranties</h2>
      <p>
        The site is provided “as is”. We do not warrant that it is accurate, complete, current
        or fit for any particular purpose, or that it will be available without interruption.
      </p>

      <h2 id="t5">5. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, we are not liable for any loss arising from use
        of, or reliance on, this site — including lost profit, lost contracts, demurrage,
        penalties or regulatory sanction. Nothing in these terms excludes liability that cannot
        lawfully be excluded.
      </p>

      <h2 id="t6">6. Your data</h2>
      <p>
        Everything you enter stays on your device. You are responsible for exporting anything
        you need to keep, and for the security of the device itself. See the
        <button class="link-inline" data-nav="/privacy" type="button">Privacy Notice</button>.
      </p>

      <h2 id="t7">7. Intellectual property</h2>
      <p>
        The site, its content, methodology and design are ours or our licensors'. You may use
        the tools and the outputs they produce for your own business purposes, including in
        documents you give to your board, your lenders or your customers. You may not
        redistribute the tools themselves, or present them as your own product, without written
        permission.
      </p>

      <h2 id="t8">8. Acceptable use</h2>
      <p>
        Do not attempt to disrupt the site, misrepresent its outputs as verified or audited, or
        use it to prepare anything unlawful. Reports prepared with the NTB Reporter should be
        factual; filing a knowingly false barrier report undermines a mechanism other operators
        depend on.
      </p>

      <h2 id="t9">9. Third-party links</h2>
      <p>
        We link to external resources such as tradebarriers.africa and the sister
        ${SITE.sister.name} practice. We are not responsible for their content or their
        practices.
      </p>

      <h2 id="t10">10. Changes</h2>
      <p>
        We may update these terms. Continued use after a change constitutes acceptance of it.
      </p>

      <h2 id="t11">11. Governing law</h2>
      <p>
        These terms are governed by the laws of ${SITE.jurisdiction}, and the courts of
        ${SITE.jurisdiction} have exclusive jurisdiction.
      </p>

      <h2 id="t12">12. Contact</h2>
      <p><a href="mailto:${SITE.email}">${SITE.email}</a> · ${SITE.location}</p>
    `
  );

  wireLinks(node);
  return node;
}

/* ============================================================
   FAQ
   ============================================================ */

export const FAQ_ITEMS = [
  {
    q: 'Is this really free, and what is the catch?',
    a: [
      'The tools are free and there is no account, no trial and no card. The commercial model is advisory work and licensing to associations — the diagnostic is how people find us, not what we sell.',
      'The honest caveat: a free diagnostic is a lead-generation instrument as well as a useful tool. We think it is a good one. You should still read the numbers with that in mind, which is why the method and its limits are published in full.'
    ]
  },
  {
    q: 'Where does my data actually go?',
    a: [
      'Nowhere. Every calculation runs in your browser and every saved result is written to your device. The app makes no network requests after the first load, and that is enforced by a content security policy rather than merely promised.',
      'The flip side: we cannot recover anything for you, and nothing syncs between devices. Export what matters.'
    ]
  },
  {
    q: 'How accurate are the benchmarks?',
    a: [
      'The rail bands come straight from published KRC SGR tariffs and are solid, but they cover line haul only — first and last mile, lift charges and ICD storage must be added before comparing rail to road.',
      'The road bands are derived from Kenya Transporters Association rate guidance and reported lane rates. They show their arithmetic and they are defensible, but they are not a dataset of observed transactions. Treat them as a reference point, not as market truth.'
    ]
  },
  {
    q: 'Why does my score change when I skip questions?',
    a: [
      'It does not — that is the point. Skipping a domain lowers the confidence figure shown beside your score rather than the score itself, because an unanswered domain counted as zero at full weight would be a made-up number.',
      'Options marked “not scored” leave the calculation entirely, so an operator with no bonded warehouse is not given a middling mark for something they do not do.'
    ]
  },
  {
    q: 'Can I use the outputs with my bank or my board?',
    a: [
      'Yes — every tool exports a print-ready PDF with the method, the sources and the date on it, which is precisely what makes it usable in a board pack.',
      'It is decision support, not an audited statement. The financeability score in particular is the arithmetic a lender runs in the first ten minutes, done in advance; it is not a credit decision and no lender is bound by it.'
    ]
  },
  {
    q: 'Does the duty calculator match what KRA will actually charge?',
    a: [
      'It models the published order of assessment: CIF, then import duty at the EAC Common External Tariff band, then excise, then VAT on that subtotal, then IDF and RDL.',
      'One genuine judgement call is exposed as a switch rather than hidden: whether IDF and RDL sit inside the VAT base. The default follows the published order, but practice varies by commodity and by how an agent lodges the entry. Reconcile against your actual entry.'
    ]
  },
  {
    q: 'Which countries does this cover?',
    a: [
      'Kenya is the only jurisdiction we treat as authoritative. Corridors terminating in Kampala, Kigali and Dar es Salaam are fully modelled for cost and transit — that is corridor data, not law — but we do not yet claim to cover Ugandan, Rwandan or Tanzanian regulation.',
      'Uganda is the first extension, and it will not ship until it has a named maintainer, because a stale regulatory register is worse than an absent one.'
    ]
  },
  {
    q: 'Does it work offline?',
    a: [
      'Yes, after the first visit. A service worker caches the application, so any screen you have opened before will open again with no signal. Your saved work is on the device and needs no connection at all.',
      'You can also install it from your browser menu and run it like an app.'
    ]
  },
  {
    q: 'Can my association or authority use this under our own brand?',
    a: [
      'Yes — white-labelling is built in, and KIFWA and KPA configurations already exist as working examples. A branded deployment changes the name, the palette and the attribution, while the method and the sources stay identical and visible.',
      'Write to us if you want one configured.'
    ]
  },
  {
    q: 'The NTB Reporter — does it file my report?',
    a: [
      'No, and it is important that this is clear. There is no server, so nothing can be transmitted on your behalf. The tool captures the barrier properly at the moment it happens and formats it for the AfCFTA mechanism at tradebarriers.africa; you lodge it there.',
      'The second half is quieter but may matter more: your own log adds up to what barriers are costing you in hours and dollars at specific points, which is the evidence an association or corridor authority can actually act on.'
    ]
  }
];

export function FaqPage() {
  const node = document.createElement('div');
  node.className = 'container-narrow page animate-fade-in';

  node.innerHTML = html`
    <header class="page-head">
      <h1>FAQ</h1>
      <p>Straight answers, including the awkward ones.</p>
    </header>
    <div class="prose">
      ${FAQ_ITEMS.map(
        (item) => html`
          <details class="faq-item">
            <summary>${item.q}</summary>
            <div class="faq-answer">${item.a.map((p) => html`<p>${p}</p>`)}</div>
          </details>
        `
      )}
      <p style="margin-top: var(--ca-space-6); font-size: var(--ca-text-sm); color: var(--ca-text-tertiary);">
        Not answered here? Write to <a href="mailto:${SITE.email}" style="color: var(--brand-accent-text);">${SITE.email}</a>,
        or read the
        <button class="link-inline" data-nav="/about" type="button">method and sources</button>
        in full.
      </p>
    </div>
  `;

  wireLinks(node);
  return node;
}

/* ---- shared ---- */

function wireLinks(node) {
  node.querySelectorAll('.link-inline').forEach((btn) => {
    btn.style.cssText =
      'background:none;border:none;padding:0;color:var(--brand-accent-text);cursor:pointer;font:inherit;text-decoration:underline;';
    btn.addEventListener('click', () => router.navigate(btn.dataset.nav));
  });

  node.querySelectorAll('[data-jump]').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const target = node.querySelector(`#${link.dataset.jump}`);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}
